import { useState } from 'react';
import type { CostPoint } from '../lib/api';

/**
 * Daily spend, as a bar chart.
 *
 * Form choice: the job is magnitude-per-day over a short window, and days are
 * discrete buckets rather than a continuous signal — bars, not a line.
 *
 * One series, so there is no legend: the title names it. Values are labelled
 * selectively (hover, plus the peak) rather than on every bar, which would be
 * unreadable at 30 days.
 */
export function CostChart({
  points,
  height = 180,
  currency = 'US$',
}: {
  points: CostPoint[];
  height?: number;
  currency?: string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  if (points.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-ink-muted"
        style={{ height }}
      >
        No spend recorded in this period.
      </div>
    );
  }

  const max = Math.max(...points.map((p) => p.amount), 0.01);
  const peakIndex = points.reduce((best, p, i) => (p.amount > points[best].amount ? i : best), 0);

  // Gap between bars is a fixed 2px of surface, per the mark spec, rather than
  // a percentage — so it stays a clean hairline at any bar count.
  const barGap = 2;

  return (
    <div className="relative">
      <div
        className="flex items-end gap-0 px-1"
        style={{ height }}
        onMouseLeave={() => setHovered(null)}
      >
        {points.map((point, index) => {
          const ratio = point.amount / max;
          // Floor of 2px so a non-zero day is never invisible.
          const barHeight = Math.max(point.amount > 0 ? 2 : 0, ratio * (height - 24));
          const isHovered = hovered === index;

          return (
            <div
              key={point.period}
              className="group relative flex flex-1 cursor-default flex-col justify-end"
              style={{ paddingLeft: barGap / 2, paddingRight: barGap / 2 }}
              onMouseEnter={() => setHovered(index)}
            >
              {/* Hit target spans the full column height, not just the bar,
                  so short bars are still easy to hover. */}
              <div className="absolute inset-0" aria-hidden />
              <div
                className="w-full transition-[filter] duration-100"
                style={{
                  height: barHeight,
                  // 4px rounded data-end, square at the baseline.
                  borderRadius: '4px 4px 0 0',
                  background:
                    index === peakIndex ? 'var(--color-series-2)' : 'var(--color-series-1)',
                  filter: isHovered ? 'brightness(1.3)' : undefined,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Baseline */}
      <div className="h-px w-full" style={{ background: 'var(--color-baseline)' }} />

      {/* Axis: first, peak and last only. Labelling all 30 days would collide. */}
      <div className="mt-1 flex justify-between px-1 text-[10px] text-ink-muted tabular">
        <span>{formatDay(points[0].period)}</span>
        {points.length > 2 && <span>{formatDay(points[peakIndex].period)}</span>}
        <span>{formatDay(points[points.length - 1].period)}</span>
      </div>

      {hovered !== null && (
        <div
          className="pointer-events-none absolute -top-1 z-10 -translate-y-full rounded-md bg-plane px-2 py-1 text-xs shadow-lg ring-1 ring-hairline"
          style={{
            left: `${((hovered + 0.5) / points.length) * 100}%`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="font-medium tabular">
            {currency}
            {points[hovered].amount.toFixed(2)}
          </div>
          <div className="text-ink-muted">{formatDay(points[hovered].period, true)}</div>
        </div>
      )}
    </div>
  );
}

function formatDay(period: string, long = false): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(long ? { weekday: 'short' } : {}),
  });
}

/**
 * Horizontal breakdown bars — spend by service or group.
 *
 * Horizontal because the category labels are words of varying length, which
 * do not fit under vertical bars without rotating them, and rotated axis
 * labels are always worse than the alternative.
 */
export function BreakdownBars({
  items,
  currency = 'US$',
  limit = 8,
}: {
  items: { key: string; amount: number }[];
  currency?: string;
  limit?: number;
}) {
  if (items.length === 0) {
    return <div className="px-4 py-8 text-center text-xs text-ink-muted">No data.</div>;
  }

  const sorted = [...items].sort((a, b) => b.amount - a.amount);
  const shown = sorted.slice(0, limit);
  const rest = sorted.slice(limit);

  // Anything beyond the limit folds into "Other" rather than being dropped —
  // a total that silently excludes rows is worse than no total.
  if (rest.length > 0) {
    shown.push({
      key: `Other (${rest.length})`,
      amount: rest.reduce((sum, r) => sum + r.amount, 0),
    });
  }

  const max = Math.max(...shown.map((s) => s.amount), 0.01);

  return (
    <div className="space-y-2 px-4 py-3">
      {shown.map((item) => (
        <div key={item.key} className="flex items-center gap-3">
          <div className="w-24 shrink-0 truncate text-xs text-ink-secondary sm:w-40" title={item.key}>
            {item.key}
          </div>
          <div className="h-4 flex-1 overflow-hidden rounded-sm bg-plane">
            <div
              className="h-full"
              style={{
                width: `${Math.max(1, (item.amount / max) * 100)}%`,
                background: 'var(--color-series-1)',
                borderRadius: '0 4px 4px 0',
              }}
            />
          </div>
          <div className="w-16 shrink-0 text-right text-xs tabular text-ink">
            {currency}
            {item.amount.toFixed(2)}
          </div>
        </div>
      ))}
    </div>
  );
}
